import React from 'react'

/**
 * Shared trust / friction-reducer row for the blog App Store CTAs.
 *
 * Why this exists (P0 conversion lever): GA4 shows the in-article hero CTA
 * fired ~1,271 impressions but only ~2 clicks, while the homepage converts
 * ~9× better on app_store_click. The single biggest structural difference
 * between the two surfaces is that the homepage carries the friction-reducing
 * proof (free, no sign-up, instant) right next to the ask, and the blog CTAs
 * were bare action buttons. A blog reader's unspoken objection to "install an
 * app" is cost + sign-up + effort — this row answers all three inline so the
 * decision is lower-stakes at the exact moment of the ask.
 *
 * Truthfulness: every claim here is one the site already makes site-wide
 * (StickyMobileAppCTA "free on iOS" + "3 scans", PricingSection "3 free
 * scans", FirstScanCallout "no signup"). We deliberately do NOT print a
 * star-rating number here — the app's rating is shown inconsistently
 * elsewhere (5.0★ vs 4.8) and a contested rating on a YMYL surface is a
 * spam-policy risk. Friction-reducers are unambiguous and convert on their
 * own.
 *
 * Rendered inside `not-prose` CTA cards, so it inherits no article typography.
 */
const ITEMS = ['Free on iOS', '3 free scans', 'No sign-up', 'Result in seconds'] as const

export interface AppCtaTrustRowProps {
  /** Tighter type + spacing for the compact hero ribbon. */
  size?: 'sm' | 'md'
  className?: string
}

export default function AppCtaTrustRow({ size = 'md', className = '' }: AppCtaTrustRowProps) {
  const text = size === 'sm' ? 'text-2xs' : 'text-xs'
  return (
    <ul
      aria-label="Free on iOS, 3 free scans, no sign-up, result in seconds"
      className={`not-prose flex flex-wrap items-center gap-x-2.5 gap-y-1 ${text} font-medium text-gray-500 ${className}`}
    >
      {ITEMS.map((item, i) => (
        <li key={item} className="inline-flex items-center gap-2">
          {i > 0 && (
            <span aria-hidden="true" className="text-gray-300">
              ·
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <svg
              className="w-3 h-3 text-[#3DDC84]"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 011.4-1.4l2.8 2.79 6.8-6.79a1 1 0 011.4 0z"
                clipRule="evenodd"
              />
            </svg>
            {item}
          </span>
        </li>
      ))}
    </ul>
  )
}

AppCtaTrustRow.displayName = 'AppCtaTrustRow'
